import { useRef, useState } from "react";
import { api, errText } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { MediaSlot } from "@/components/admin/MediaSlot";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";

// The square VSL shown in the landing page hero, plus its optional poster frame.
export const VslManager = ({ settings }) => {
  const [vsl, setVsl] = useState(settings.vsl || null);
  const [posterId, setPosterId] = useState(settings.vsl_poster_id || null);
  const [busy, setBusy] = useState(false);
  const posterRef = useRef();

  const uploadPoster = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/media/vsl-poster", fd);
      setPosterId(data.value);
      toast.success("Poster updated");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
      if (posterRef.current) posterRef.current.value = "";
    }
  };

  const removePoster = async () => {
    await api.delete("/media/slots/vsl_poster_id");
    setPosterId(null);
    toast.success("Poster removed");
  };

  return (
    <div className="grid md:grid-cols-[1fr_260px] gap-5 items-start">
      <MediaSlot
        slot="vsl"
        label="Video sales letter (1:1 square)"
        hint="Visitors press play themselves — never autoplays with sound. Upload the file for full VSLPlay / 25 / 50 / 75% tracking."
        value={vsl}
        onChange={setVsl}
        square
        poster={posterId ? mediaUrl(posterId) : undefined}
      />
      <div className="border border-gray-200 p-4 space-y-3" data-testid="vsl-poster-panel">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-gray-500">Poster frame</p>
            <p className="text-xs text-gray-400 mt-1">Shown before play. Square image works best.</p>
          </div>
          {posterId && (
            <button data-testid="vsl-poster-remove" onClick={removePoster} className="text-gray-400 hover:text-red-600 transition-colors duration-150">
              <Trash2 size={15} />
            </button>
          )}
        </div>
        {posterId ? (
          <img src={mediaUrl(posterId)} alt="VSL poster" className="w-full aspect-square object-cover bg-black" data-testid="vsl-poster-preview" />
        ) : (
          <div className="w-full aspect-square bg-[#FAF7F2] border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">No poster</div>
        )}
        <label className="flex items-center gap-2 text-xs uppercase tracking-wider border border-gray-300 h-10 px-4 cursor-pointer hover:bg-gray-50 transition-colors duration-150 w-fit">
          <ImagePlus size={13} /> {busy ? "Uploading…" : posterId ? "Replace poster" : "Upload poster"}
          <input ref={posterRef} data-testid="vsl-poster-file" type="file" accept="image/*,.heic,.heif" className="hidden" disabled={busy} onChange={(e) => uploadPoster(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
};
